/**
 * =============================================================================
 * ===== FILE: arena.js — DUNIA / LATAR LINTASAN =====
 * =============================================================================
 * Menggambar: langit, matahari, awan, tanah, rumput, dekorasi, start/finish,
 * kupu-kupu, lebah, burung, daun. Lazy render (hanya yang di viewport).
 * =============================================================================
 */

import { rand, randInt, CONFIG, getTrackPadding } from './config.js';

/**
 * Ant-world arena with lazy/offscreen-aware decorations & ambient critters.
 */
export class Arena {
  /** Buat arena default dan seed dekorasi awal. */
  constructor() {
    this.trackLength = CONFIG.DISTANCES.classic;
    this.height = 600;
    this.decor = [];
    this.critters = [];
    this.clouds = [];
    this.time = 0;
    this._seedDecor();
  }

  /** Set panjang lintasan + tinggi viewport, lalu generate ulang dekorasi. */
  setTrack(length, height) {
    this.trackLength = length;
    this.height = height;
    this._seedDecor();
  }

  /**
   * _seedDecor()
   * Sebar batu, daun, jamur, anthill, critter, awan di sepanjang track.
   */
  _seedDecor() {
    this.decor = [];
    this.critters = [];
    this.clouds = [];

    const L = this.trackLength;
    const h = this.height;

    // Ground patches & props along the track
    const count = Math.floor(L / 90);
    for (let i = 0; i < count; i++) {
      const x = 80 + i * 90 + rand(-20, 20);
      const kind = pickKind();
      const y =
        kind === 'hill' || kind === 'mushroom' || kind === 'flower' || kind === 'rock'
          ? rand(h * 0.12, h * 0.88)
          : rand(h * 0.15, h * 0.85);
      this.decor.push({
        kind,
        x,
        y,
        s: rand(0.7, 1.4),
        rot: rand(-0.3, 0.3),
        shade: rand(0, 1),
      });
    }

    // Start anthill
    this.decor.push({ kind: 'anthill', x: 30, y: h * 0.5, s: 1.4, rot: 0, shade: 0.5 });
    // Occasional extra hills
    for (let i = 0; i < 3; i++) {
      this.decor.push({
        kind: 'anthill',
        x: rand(L * 0.2, L * 0.85),
        y: rand(h * 0.2, h * 0.8),
        s: rand(0.8, 1.2),
        rot: 0,
        shade: rand(0, 1),
      });
    }

    // Ambient critters
    for (let i = 0; i < 10; i++) {
      this.critters.push({
        kind: Math.random() < 0.55 ? 'butterfly' : Math.random() < 0.5 ? 'bee' : 'bird',
        x: rand(0, L),
        y: rand(40, h - 40),
        vx: rand(20, 55) * (Math.random() < 0.5 ? 1 : -1),
        phase: rand(0, Math.PI * 2),
        s: rand(0.7, 1.2),
      });
    }

    for (let i = 0; i < 8; i++) {
      this.clouds.push({
        x: rand(0, L),
        y: rand(20, h * 0.35),
        s: rand(0.8, 1.8),
        vx: rand(4, 14),
        a: rand(0.25, 0.5),
      });
    }

    // Floating leaves
    for (let i = 0; i < 18; i++) {
      this.critters.push({
        kind: 'leaf',
        x: rand(0, L),
        y: rand(0, h),
        vx: rand(-15, -5),
        phase: rand(0, Math.PI * 2),
        s: rand(0.6, 1.3),
      });
    }
  }

  /** Gerakkan critter & awan tiap frame. */
  update(dt) {
    this.time += dt;
    const L = this.trackLength;
    const h = this.height;

    for (const c of this.critters) {
      c.phase += dt * 3;
      if (c.kind === 'leaf') {
        c.x += c.vx * dt;
        c.y += Math.sin(c.phase) * 18 * dt;
        if (c.x < -40) {
          c.x = L + 40;
          c.y = rand(0, h);
        }
      } else if (c.kind === 'bird') {
        c.x += c.vx * dt;
        c.y += Math.sin(c.phase * 0.5) * 10 * dt;
        if (c.x > L + 60) c.x = -60;
        if (c.x < -60) c.x = L + 60;
      } else {
        c.x += c.vx * dt;
        c.y += Math.sin(c.phase) * 22 * dt;
        if (c.x > L + 50) c.x = -50;
        if (c.x < -50) c.x = L + 50;
        c.y = Math.max(20, Math.min(h - 20, c.y));
      }
    }

    for (const cl of this.clouds) {
      cl.x += cl.vx * dt;
      if (cl.x > L + 100) cl.x = -100;
    }
  }

  /**
   * draw(ctx, camera)
   * Gambar full background + track + finish (dipanggil di world-space).
   */
  draw(ctx, camera) {
    const w = camera.width;
    const h = this.height;
    const camX = camera.x;

    // Sky gradient (screen space strip, then world ground)
    const sky = ctx.createLinearGradient(0, 0, 0, h);
    sky.addColorStop(0, '#7ec8e3');
    sky.addColorStop(0.45, '#b8e0d2');
    sky.addColorStop(1, '#6b8f3c');
    ctx.fillStyle = sky;
    ctx.fillRect(camX, 0, w, h);

    // Sun
    const sunX = camX + w * 0.82;
    const sunY = 70;
    const sg = ctx.createRadialGradient(sunX, sunY, 10, sunX, sunY, 80);
    sg.addColorStop(0, 'rgba(255,236,150,0.95)');
    sg.addColorStop(0.4, 'rgba(255,200,80,0.35)');
    sg.addColorStop(1, 'rgba(255,200,80,0)');
    ctx.fillStyle = sg;
    ctx.beginPath();
    ctx.arc(sunX, sunY, 80, 0, Math.PI * 2);
    ctx.fill();

    // Clouds
    for (const cl of this.clouds) {
      if (!camera.inView(cl.x, 120)) continue;
      drawCloud(ctx, cl.x, cl.y, cl.s, cl.a);
    }

    // Dirt track band (padding adapts on mobile)
    const padY = getTrackPadding(h);
    const trackTop = padY - 24;
    const trackBot = h - padY + 24;
    const dirt = ctx.createLinearGradient(0, trackTop, 0, trackBot);
    dirt.addColorStop(0, '#7a5c3a');
    dirt.addColorStop(0.5, '#8b6914');
    dirt.addColorStop(1, '#6b4423');
    ctx.fillStyle = dirt;
    ctx.fillRect(camX - 20, trackTop, w + 40, trackBot - trackTop);

    // Grass edges
    ctx.fillStyle = '#4a7c23';
    ctx.fillRect(camX - 20, 0, w + 40, trackTop + 8);
    ctx.fillRect(camX - 20, trackBot - 8, w + 40, h - trackBot + 20);

    // Grass blades near edges (lazy)
    ctx.strokeStyle = '#3d6b1e';
    ctx.lineWidth = 1.5;
    const bladeStart = Math.floor(camX / 12) * 12;
    for (let x = bladeStart; x < camX + w + 20; x += 12) {
      const sway = Math.sin(this.time * 2 + x * 0.05) * 3;
      ctx.beginPath();
      ctx.moveTo(x, trackTop + 6);
      ctx.quadraticCurveTo(x + sway, trackTop - 8, x + sway * 1.5, trackTop - 16);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, trackBot - 6);
      ctx.quadraticCurveTo(x - sway, trackBot + 8, x - sway * 1.5, trackBot + 16);
      ctx.stroke();
    }

    // Lane dashes
    const laneCount = Math.max(2, this.laneCount || 8);
    const pad = getTrackPadding(h);
    const usable = Math.max(140, h - pad * 2);
    const laneH = usable / laneCount;
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    ctx.setLineDash([8, 12]);
    for (let i = 1; i < laneCount; i++) {
      const y = pad + laneH * i;
      ctx.beginPath();
      ctx.moveTo(camX, y);
      ctx.lineTo(camX + w, y);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // Dirt texture dots (cheap)
    ctx.fillStyle = 'rgba(60,40,20,0.15)';
    const seed = Math.floor(camX / 40);
    for (let i = 0; i < 40; i++) {
      const px = camX + ((seed * 17 + i * 37) % Math.floor(w));
      const py = trackTop + 20 + ((i * 53) % Math.floor(trackBot - trackTop - 40));
      ctx.fillRect(px, py, 2, 2);
    }

    // Decorations
    for (const d of this.decor) {
      if (!camera.inView(d.x, 60)) continue;
      drawDecor(ctx, d);
    }

    // Start line
    drawGate(ctx, 50, trackTop, trackBot - trackTop, 'START', '#06d6a0');

    // Finish line — only "exists" visually; camera reveals it near end
    drawFinish(ctx, this.trackLength, trackTop, trackBot - trackTop);

    // Critters
    for (const c of this.critters) {
      if (!camera.inView(c.x, 50)) continue;
      drawCritter(ctx, c, this.time);
    }
  }
}

function pickKind() {
  const r = Math.random();
  if (r < 0.2) return 'rock';
  if (r < 0.35) return 'leaf';
  if (r < 0.48) return 'twig';
  if (r < 0.6) return 'flower';
  if (r < 0.72) return 'mushroom';
  if (r < 0.82) return 'pebble';
  if (r < 0.92) return 'hole';
  return 'grassclump';
}

function drawCloud(ctx, x, y, s, a) {
  ctx.fillStyle = `rgba(255,255,255,${a})`;
  ctx.beginPath();
  ctx.arc(x, y, 16 * s, 0, Math.PI * 2);
  ctx.arc(x + 18 * s, y - 6 * s, 20 * s, 0, Math.PI * 2);
  ctx.arc(x + 38 * s, y, 14 * s, 0, Math.PI * 2);
  ctx.arc(x + 18 * s, y + 6 * s, 14 * s, 0, Math.PI * 2);
  ctx.fill();
}

function drawDecor(ctx, d) {
  const { x, y, s, rot, kind, shade } = d;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(s, s);

  switch (kind) {
    case 'rock':
      ctx.fillStyle = `hsl(30, 8%, ${35 + shade * 15}%)`;
      ctx.beginPath();
      ctx.moveTo(-10, 4);
      ctx.quadraticCurveTo(-12, -8, 0, -10);
      ctx.quadraticCurveTo(12, -8, 10, 4);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.beginPath();
      ctx.ellipse(-3, -4, 3, 2, -0.4, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'pebble':
      ctx.fillStyle = `hsl(25, 10%, ${40 + shade * 10}%)`;
      ctx.beginPath();
      ctx.ellipse(0, 0, 5, 3.5, 0.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'leaf':
      ctx.fillStyle = `hsl(${100 + shade * 40}, 55%, 40%)`;
      ctx.beginPath();
      ctx.ellipse(0, 0, 14, 7, 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(40,80,20,0.5)';
      ctx.beginPath();
      ctx.moveTo(-10, 0);
      ctx.lineTo(10, 0);
      ctx.stroke();
      break;
    case 'twig':
      ctx.strokeStyle = '#6b4226';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-14, 2);
      ctx.quadraticCurveTo(0, -4, 14, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(2, -2);
      ctx.lineTo(6, -10);
      ctx.stroke();
      break;
    case 'flower':
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        ctx.fillStyle = i % 2 ? '#ff8fab' : '#ffc8dd';
        ctx.beginPath();
        ctx.ellipse(Math.cos(a) * 6, Math.sin(a) * 6, 5, 3, a, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = '#ffd166';
      ctx.beginPath();
      ctx.arc(0, 0, 3.5, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'mushroom':
      ctx.fillStyle = '#f8f1e3';
      ctx.fillRect(-3, -2, 6, 10);
      ctx.fillStyle = shade > 0.5 ? '#e63946' : '#f4a261';
      ctx.beginPath();
      ctx.ellipse(0, -4, 10, 7, 0, Math.PI, 0);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(-4, -6, 1.8, 0, Math.PI * 2);
      ctx.arc(3, -5, 1.4, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'hole':
      ctx.fillStyle = 'rgba(20,10,5,0.55)';
      ctx.beginPath();
      ctx.ellipse(0, 0, 12, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(80,50,30,0.5)';
      ctx.stroke();
      break;
    case 'grassclump':
      ctx.strokeStyle = '#2d6a1e';
      ctx.lineWidth = 1.8;
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(i * 3, 4);
        ctx.quadraticCurveTo(i * 3 + 2, -4, i * 4, -12);
        ctx.stroke();
      }
      break;
    case 'anthill':
      ctx.fillStyle = '#a67c52';
      ctx.beginPath();
      ctx.moveTo(-28, 16);
      ctx.quadraticCurveTo(-20, -18, 0, -22);
      ctx.quadraticCurveTo(20, -18, 28, 16);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgba(40,25,10,0.55)';
      ctx.beginPath();
      ctx.ellipse(0, -8, 7, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(0,0,0,0.15)';
      ctx.beginPath();
      ctx.ellipse(0, 16, 26, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    default:
      break;
  }
  ctx.restore();
}

function drawGate(ctx, x, top, height, label, color) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.fillRect(x - 3, top, 6, height);
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.fillRect(x - 14, top - 22, 28, 18);
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 10px Nunito, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(label, x, top - 9);
  ctx.restore();
}

function drawFinish(ctx, x, top, height) {
  const colW = 10;
  const rowH = 12;
  const cols = 2;
  const rows = Math.ceil(height / rowH);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      ctx.fillStyle = (r + c) % 2 === 0 ? '#1a1a1a' : '#f8f9fa';
      ctx.fillRect(x + c * colW, top + r * rowH, colW, rowH);
    }
  }
  // Posts
  ctx.fillStyle = '#ffd166';
  ctx.fillRect(x - 4, top - 30, 8, 30);
  ctx.fillRect(x + cols * colW - 4, top - 30, 8, 30);
  ctx.fillStyle = '#ef476f';
  ctx.fillRect(x - 4, top - 30, cols * colW + 8, 10);
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 11px Fredoka, Nunito, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('FINISH', x + cols * colW / 2, top - 22);

  // Flag
  ctx.fillStyle = '#ef476f';
  ctx.beginPath();
  ctx.moveTo(x + cols * colW + 4, top - 28);
  ctx.lineTo(x + cols * colW + 28, top - 20);
  ctx.lineTo(x + cols * colW + 4, top - 12);
  ctx.fill();
}

function drawCritter(ctx, c, t) {
  ctx.save();
  ctx.translate(c.x, c.y);
  const s = c.s;
  if (c.kind === 'butterfly') {
    const flap = 0.5 + Math.sin(c.phase * 4) * 0.45;
    ctx.scale(c.vx >= 0 ? 1 : -1, 1);
    ctx.fillStyle = '#c77dff';
    ctx.beginPath();
    ctx.ellipse(-4 * s, 0, 8 * s * flap, 10 * s, -0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ff8fab';
    ctx.beginPath();
    ctx.ellipse(4 * s, 0, 8 * s * flap, 10 * s, 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#333';
    ctx.fillRect(-1 * s, -6 * s, 2 * s, 12 * s);
  } else if (c.kind === 'bee') {
    ctx.scale(c.vx >= 0 ? 1 : -1, 1);
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.beginPath();
    ctx.ellipse(-2 * s, -4 * s, 5 * s, 3 * s, -0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffd166';
    ctx.beginPath();
    ctx.ellipse(0, 0, 7 * s, 5 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 1.5 * s;
    ctx.beginPath();
    ctx.moveTo(-2 * s, -4 * s);
    ctx.lineTo(-2 * s, 4 * s);
    ctx.moveTo(2 * s, -4 * s);
    ctx.lineTo(2 * s, 4 * s);
    ctx.stroke();
  } else if (c.kind === 'bird') {
    ctx.strokeStyle = '#33415c';
    ctx.lineWidth = 2 * s;
    ctx.lineCap = 'round';
    const wing = Math.sin(t * 8 + c.phase) * 6 * s;
    ctx.beginPath();
    ctx.moveTo(-10 * s, wing);
    ctx.quadraticCurveTo(0, -wing * 0.5, 10 * s, wing);
    ctx.stroke();
  } else if (c.kind === 'leaf') {
    ctx.rotate(c.phase);
    ctx.fillStyle = `hsla(${90 + (c.phase % 1) * 40}, 50%, 42%, 0.75)`;
    ctx.beginPath();
    ctx.ellipse(0, 0, 9 * s, 4 * s, 0.4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}
